import {
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  MapPinIcon,
  ShieldCheckIcon,
  ClockIcon,
  IdentificationIcon,
  WifiIcon,
  CpuChipIcon,
  UserIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

// Country flag emoji helper (uses regional indicator symbols)
function getCountryFlag(countryCode) {
  if (!countryCode || countryCode.length !== 2) return null;
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

// Calculate time remaining until expiry with smart formatting
function getTimeRemaining(expTimestamp) {
  if (!expTimestamp) return null;

  const now = Date.now();
  const expTime = new Date(expTimestamp).getTime();
  const diff = expTime - now;

  if (diff <= 0) return { text: 'Expired', level: 'expired' };

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  // Format based on duration
  let text;
  let level;

  if (years >= 1) {
    const remainingMonths = Math.floor((days % 365) / 30);
    text = remainingMonths > 0 ? `${years}y ${remainingMonths}mo` : `${years} year`;
    level = 'excellent';
  } else if (months >= 1) {
    const remainingDays = days % 30;
    text = remainingDays > 0 ? `${months}mo ${remainingDays}d` : `${months} month${months > 1 ? 's' : ''}`;
    level = months >= 6 ? 'excellent' : months >= 3 ? 'good' : 'warning';
  } else if (days >= 1) {
    const remainingHours = hours % 24;
    text = `${days}d ${remainingHours}h`;
    level = days >= 14 ? 'good' : days >= 7 ? 'warning' : 'critical';
  } else if (hours >= 1) {
    const remainingMins = minutes % 60;
    text = `${hours}h ${remainingMins}m`;
    level = 'critical';
  } else {
    text = `${minutes}m`;
    level = 'critical';
  }

  return { text, level };
}

// Get badge color based on expiry level
function getExpiryBadgeColor(level) {
  switch (level) {
    case 'excellent': return 'green';
    case 'good': return 'blue';
    case 'warning': return 'yellow';
    case 'critical': return 'red';
    case 'expired': return 'red';
    default: return 'gray';
  }
}

// Device type icon selector
function DeviceTypeIcon({ type, className }) {
  switch (type) {
    case 'mobile':
      return <DevicePhoneMobileIcon className={className} />;
    case 'tablet':
      return <DeviceTabletIcon className={className} />;
    default:
      return <ComputerDesktopIcon className={className} />;
  }
}

// Info card component
function InfoCard({ icon: Icon, title, children, className = '' }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-xl p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
          <Icon className="w-4 h-4 text-gray-600" />
        </div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      </div>
      {children}
    </div>
  );
}

// Detail row component
function DetailRow({ label, value, mono = false, badge = false, badgeColor = 'gray' }) {
  if (!value && value !== false) return null;

  const badgeColors = {
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    blue: 'bg-blue-100 text-blue-700',
    gray: 'bg-gray-100 text-gray-700',
  };

  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-gray-500">{label}</span>
      {badge ? (
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badgeColors[badgeColor]}`}>
          {String(value)}
        </span>
      ) : (
        <span className={`text-sm text-gray-900 ${mono ? 'font-mono text-xs' : ''}`}>
          {String(value)}
        </span>
      )}
    </div>
  );
}

export default function ConnectionContextPanel({ context }) {
  if (!context) return null;

  const { device, connection, geo, security, account } = context;
  const hasContent = device || connection || geo || security || account;

  if (!hasContent) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
        <WifiIcon className="w-4 h-4" />
        Connection Context
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Device Info */}
        {device && (
          <InfoCard icon={ComputerDesktopIcon} title="Device Information">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                device.isMobile ? 'bg-blue-100' : 'bg-gray-100'
              }`}>
                <DeviceTypeIcon
                  type={device.type}
                  className={`w-6 h-6 ${device.isMobile ? 'text-blue-600' : 'text-gray-600'}`}
                />
              </div>
              <div>
                <p className="font-medium text-gray-900 capitalize">
                  {device.model || device.type || 'Unknown'}
                </p>
                <p className="text-xs text-gray-500">
                  {device.browser} {device.browserVersion && `v${device.browserVersion}`}
                </p>
              </div>
            </div>
            <div className="border-t border-gray-100 pt-2">
              {device.model && (
                <DetailRow label="Device Model" value={device.model} />
              )}
              <DetailRow label="Device Type" value={device.type} />
              <DetailRow label="Operating System" value={device.os} />
              <DetailRow label="OS Version" value={device.osVersion} />
              <DetailRow label="Browser" value={device.browser} />
              <DetailRow label="Browser Version" value={device.browserVersion} mono />
              <DetailRow
                label="Mobile Device"
                value={device.isMobile ? 'Yes' : 'No'}
                badge
                badgeColor={device.isMobile ? 'blue' : 'gray'}
              />
            </div>
          </InfoCard>
        )}

        {/* Geolocation */}
        {geo && (
          <InfoCard icon={MapPinIcon} title="Location">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-2xl">
                {geo.isPrivate ? '🏠' : (getCountryFlag(geo.country) || <MapPinIcon className="w-6 h-6 text-green-600" />)}
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {geo.city || geo.countryName || (geo.isPrivate ? 'Local Network' : 'Unknown')}
                </p>
                <p className="text-xs text-gray-500">
                  {geo.isPrivate ? 'Private IP Address' : (geo.countryName || geo.country || '')}
                </p>
              </div>
            </div>
            <div className="border-t border-gray-100 pt-2">
              <DetailRow label="Country" value={geo.countryName || geo.country} />
              <DetailRow label="Region" value={geo.region} />
              <DetailRow label="City" value={geo.city} />
              <DetailRow label="Timezone" value={geo.timezone} />
              <DetailRow
                label="Private Network"
                value={geo.isPrivate ? 'Yes' : 'No'}
                badge
                badgeColor={geo.isPrivate ? 'yellow' : 'gray'}
              />
            </div>
          </InfoCard>
        )}

        {/* Connection Details */}
        {connection && (
          <InfoCard icon={GlobeAltIcon} title="Connection">
            <div className="space-y-0">
              <DetailRow label="Connection ID" value={connection.id} mono />
              <DetailRow label="Language" value={connection.language} />
              <DetailRow label="Origin" value={connection.origin} mono />
              <DetailRow label="Protocol" value={connection.protocol} />
              <DetailRow
                label="Secure (HTTPS/WSS)"
                value={connection.isSecure ? 'Yes' : 'No'}
                badge
                badgeColor={connection.isSecure ? 'green' : 'red'}
              />
            </div>
          </InfoCard>
        )}

        {/* Security Context */}
        {security && (
          <InfoCard icon={LockClosedIcon} title="Security">
            <div className="space-y-0">
              <DetailRow label="Token Issuer" value={security.tokenIssuer} />
              {security.tokenExp && (() => {
                const expiry = getTimeRemaining(security.tokenExp);
                return (
                  <>
                    <DetailRow
                      label="Token Expires"
                      value={new Date(security.tokenExp).toLocaleString()}
                    />
                    <DetailRow
                      label="Expires In"
                      value={expiry?.text}
                      badge
                      badgeColor={getExpiryBadgeColor(expiry?.level)}
                    />
                  </>
                );
              })()}
              {security.tokenIat && (
                <DetailRow
                  label="Token Issued"
                  value={new Date(security.tokenIat).toLocaleString()}
                />
              )}
            </div>
          </InfoCard>
        )}

        {/* Account Context */}
        {account && (
          <InfoCard icon={UserIcon} title="Account">
            <div className="space-y-0">
              <DetailRow
                label="Subscription"
                value={account.subscription ? 'Active' : 'Inactive'}
                badge
                badgeColor={account.subscription ? 'green' : 'yellow'}
              />
              <DetailRow label="Devices Count" value={account.devicesCount} />
              <DetailRow label="Default Device" value={account.defaultDevice} mono />
            </div>
          </InfoCard>
        )}
      </div>
    </div>
  );
}
